/**
 * Business collections — a named, curated group of businesses (e.g. "Best
 * Coffee Spots").
 *
 * The collection endpoint returns the collection *and* its members in one call:
 *   /api/v1/business-collections/{id}?includeBusinessDetails=<bool>
 * With `includeBusinessDetails=false` each member carries only its businessId
 * (every other field is null); with `true` they're complete business records.
 *
 * The name-search endpoint may instead return `collection` as a plain array of
 * businessIds, so both shapes are tolerated — see the converter.
 */

import { BusinessProfile, RawBusiness } from "./BusinessObject";

export interface BusinessCollection {
  /** Collection id (uuid). */
  uid: string;
  creatorId: string;
  /** Curator's display name, when the backend sends one. */
  creatorName?: string | null;
  name: string;
  /** businessIds belonging to this collection. */
  businessIds: string[];
  bannerUrl?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

/** One collection plus its resolved members. */
export interface BusinessCollectionDetail {
  collection: BusinessCollection;
  businesses: BusinessProfile[];
}

// ─── Create / update ─────────────────────────────────────────────────────────

/**
 * Body for POST /api/v1/business-collections. `bannerUrl` is required
 * (non-nullable) by design — the Create Collection form makes banner
 * upload+crop a mandatory step, unlike the read-side `BusinessCollection`
 * type above where an already-created collection could in principle have a
 * null banner (e.g. seeded outside this UI).
 */
export interface CreateCollectionPayload {
  collectionName: string;
  businessIds: string[];
  bannerUrl: string;
}

/** Body for PUT /api/v1/business-collections/{id}. Full replace — the backend
 *  contract takes the same three fields as create, not a partial patch. */
export interface UpdateCollectionPayload extends CreateCollectionPayload {
  uid: string;
}

// Raw shapes from the backend — converted before touching Redux.

/**
 * A member record. Deliberately NOT `RawBusiness`: with
 * `includeBusinessDetails=false` the backend sends every field except
 * `businessId` as null, which `RawBusiness` (non-nullable `name`,
 * `listingType`, `phone`) would misrepresent. Only `businessId` is guaranteed.
 */
export type RawBusinessCollectionMemberDetail = Partial<
  Omit<RawBusiness, "businessId">
> & {
  businessId: string;
};

/** A member entry: a bare businessId, or a (possibly sparse) business record. */
export type RawBusinessCollectionMember =
  | string
  | RawBusinessCollectionMemberDetail;

export interface RawBusinessCollection {
  uid: string;
  creatorId?: string;
  creatorName?: string | null;
  collectionName?: string;
  collection?: RawBusinessCollectionMember[] | null;
  bannerUrl?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface RawBusinessCollectionListResponse {
  contents: RawBusinessCollection[] | null;
}

// ─── Errors ──────────────────────────────────────────────────────────────────

/** Backend error codes for the business-collection endpoints. */
export const CollectionErrorCodes = {
  /** Wrong/unknown id — not retryable, so the UI shows a "not found" screen. */
  NOT_FOUND: "13001",
  VALIDATION: "13002",
  NAME_REQUIRED: "13003",
  INTERNAL: "13999",
} as const;

export type CollectionErrorCode =
  (typeof CollectionErrorCodes)[keyof typeof CollectionErrorCodes];

/**
 * The failure envelope these endpoints return — note they report failures
 * inside a 2xx body, so `response.ok` alone isn't enough to trust a payload.
 */
export interface CollectionErrorEnvelope {
  errorInformation?:
    | { errorCode?: string; errorDetail?: string | null }[]
    | null;
  generalProcessingStatus?: {
    processingStatus?: string;
    processingDetails?: string | null;
  } | null;
}
